// What the builder's person on the site camera may receive from a customer's screen (CR-20261009-001 L3.1,
// g-site-privacy, US-3.1.7). ONLY on an RM-hosted meeting (D532 A, Ravi 9 Oct): a meeting a Mana sales manager hosts
// — every meeting Helix stamps NULL, every one live today — keeps today's behaviour byte for byte (her status and
// requests broadcast, no subscription permissions, stills to everyone). On an RM-hosted meeting the site phone hears
// the voices; it never receives her camera or her family's, her status, her requests (cost sheet, booking, signals), a
// still, the brief, or live assist.
//
// Two layers: what her page SENDS (destination lists, below), and what the SFU lets a site-* identity SUBSCRIBE to
// (guardTracks) — the second holds even against a raw client that tries to subscribe past the page.
//
// T-L3.1-13 (tests/e2e/site-privacy.spec.mjs, a raw livekit-client against a local LiveKit) is REQUIRED before
// CLIKHOME_RM_HOSTS_CANVAS is switched on: these rules are what it proves.

// The host kind, read from the link answer the page already has (GET /api/link/:code → meeting): Helix sends
// siteVia "helix" and the brand ONLY on a meeting its record stamps canvas_host_kind='rm' (canvas_room.py
// _build_rm_hosted_request; context/20 §1). Either present → "rm"; neither → null, today's meeting. Never a switch.
export const hostKindOf = (meeting) => (meeting && (meeting.siteVia === "helix" || (typeof meeting.brand === "string" && meeting.brand.trim())) ? "rm" : null);
export const rmHosted = (meeting) => hostKindOf(meeting) === "rm";

const ids = (room) => [...(room?.remoteParticipants?.values?.() || [])].map((p) => String(p?.identity || "")).filter(Boolean);

export const isSite = (identity) => String(identity || "").startsWith("site-");

// Everyone in the room but the site phone.
export const notSite = (room) => ids(room).filter((i) => !isSite(i));

// The host and the RM: the only screens that act on her requests.
export const hostAndRm = (room) => ids(room).filter((i) => i.startsWith("host-") || i.startsWith("rm-"));

// LiveKit reads an empty destinationIdentities as "everyone" — so an empty list must send nothing at all.
export async function sendOnlyTo(send, topic, body, to, opts = {}) {
  if (!Array.isArray(to) || !to.length) return false;
  return send(topic, body, { ...opts, to });
}

// The SFU's subscription permissions for her tracks: a site-* identity her microphone only (none until she has one);
// everyone else everything.
export function sitePermissions(identities, micSid) {
  return identities.map((participantIdentity) => (isSite(participantIdentity)
    ? { participantIdentity, allowAll: false, allowedTrackSids: micSid ? [micSid] : [] }
    : { participantIdentity, allowAll: true }));
}

const micSidOf = (room) => {
  for (const pub of room?.localParticipant?.trackPublications?.values?.() || []) {
    if (pub?.source === "microphone" && pub.trackSid) return pub.trackSid;
  }
  return null;
};

// Applied on connect and whenever the room or her tracks change. Everyone-allowed is switched off, so each person must
// be named: the visible ones from the room, and the two HIDDEN listeners — live assist (`assist-<meeting id>`, which
// also writes the recording, D145) and the scribe — which no page can list, from the room's name (`ms-<meeting id>`).
// A person who joins between two updates waits for the next (ParticipantConnected), never the other way round.
export function guardTracks(room) {
  try {
    const name = String(room?.name || "");
    const meetingId = name.startsWith("ms-") ? name.slice(3) : "";
    const hidden = meetingId ? [`assist-${meetingId}`, `scribe-${meetingId}`] : [];
    const everyone = [...ids(room), ...hidden.filter((h) => !ids(room).includes(h))];
    const out = room.localParticipant.setTrackSubscriptionPermissions(false, sitePermissions(everyone, micSidOf(room)));
    if (out && typeof out.catch === "function") out.catch(() => {});
  } catch {
    /* not connected (yet): the next event applies it */
  }
}

// Her screen's three privacy points, by the meeting. A NULL meeting's are exactly today's: `send(topic, body)` to
// everyone, and no subscription permissions at all.
export function customerPrivacy({ send, room, meeting }) {
  if (!rmHosted(meeting)) {
    return { status: (topic, body) => send(topic, body), toStaff: (topic, body) => send(topic, body), guard: () => {} };
  }
  return {
    status: (topic, body) => sendOnlyTo(send, topic, body, notSite(room)),
    toStaff: (topic, body) => sendOnlyTo(send, topic, body, hostAndRm(room)),
    guard: () => guardTracks(room),
  };
}

// Whether a still may go to this screen: on an RM-hosted meeting never to the site phone; on today's, to everyone.
export const stillGoesTo = (identity, meeting) => !(rmHosted(meeting) && isSite(identity));
